import React, { useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTroncRecords } from "../../../hooks/useTroncRecords";
import { useEmployees } from "../../../hooks/useEmployees";
import { useLocationContext } from "../../../contexts/LocationContext";
import { useTenantContext } from "../../../contexts/TenantContext";

/**
 * HMRC Compliance tab — compliance reporting for TRONC scheme.
 * Shows eligibility, distribution audit, and generates compliance-ready exports.
 */
const HmrcComplianceTab = ({ shouldFetchData }) => {
  const { currentLocation } = useLocationContext();
  const { selectedTenant } = useTenantContext();
  const { records: troncRecords, loading: loadingTronc } = useTroncRecords({
    autoFetch: shouldFetchData,
  });
  const { employees, loading: loadingEmployees } = useEmployees({
    autoFetch: shouldFetchData,
  });

  const [section, setSection] = useState("overview"); // overview | eligibility | audit

  const loading = loadingTronc || loadingEmployees;

  const allEmployees = useMemo(() => {
    let list = employees || [];
    if (currentLocation)
      list = list.filter((e) => e.locationId === currentLocation.id);
    return list;
  }, [employees, currentLocation]);

  const eligibleEmployees = useMemo(
    () => allEmployees.filter((e) => e.isEligibleForTronc && e.isActive),
    [allEmployees],
  );
  const ineligibleEmployees = useMemo(
    () => allEmployees.filter((e) => !e.isEligibleForTronc && e.isActive),
    [allEmployees],
  );
  const inactiveEmployees = useMemo(
    () => allEmployees.filter((e) => !e.isActive),
    [allEmployees],
  );

  const distributed = useMemo(() => {
    let list = (troncRecords || []).filter(
      (r) => r.isDistributed || r.status === "Distributed" || r.status === 3,
    );
    if (currentLocation)
      list = list.filter((r) => r.locationId === currentLocation.id);
    return list;
  }, [troncRecords, currentLocation]);

  const pending = useMemo(() => {
    let list = (troncRecords || []).filter(
      (r) => !r.isDistributed && r.status !== "Distributed" && r.status !== 3,
    );
    if (currentLocation)
      list = list.filter((r) => r.locationId === currentLocation.id);
    return list;
  }, [troncRecords, currentLocation]);

  const totalDistributed = useMemo(
    () => distributed.reduce((s, r) => s + (r.amount || 0), 0),
    [distributed],
  );
  const totalPending = useMemo(
    () => pending.reduce((s, r) => s + (r.amount || 0), 0),
    [pending],
  );

  // Tax year bounds (UK tax year: 6 Apr – 5 Apr)
  const taxYear = useMemo(() => {
    const now = new Date();
    const year =
      now.getMonth() >= 3 && now.getDate() >= 6
        ? now.getFullYear()
        : now.getFullYear() - 1;
    return {
      label: `${year}/${year + 1}`,
      from: new Date(year, 3, 6),
      to: new Date(year + 1, 3, 5),
    };
  }, []);

  const taxYearDistributed = useMemo(() => {
    return distributed.filter((r) => {
      const d = new Date(r.periodStart);
      return d >= taxYear.from && d <= taxYear.to;
    });
  }, [distributed, taxYear]);

  const taxYearTotal = useMemo(
    () => taxYearDistributed.reduce((s, r) => s + (r.amount || 0), 0),
    [taxYearDistributed],
  );

  // Compliance checks
  const complianceChecks = useMemo(() => {
    const checks = [];

    // 1. All employees have NI numbers
    const missingNI = eligibleEmployees.filter(
      (e) => !e.nationalInsuranceNumber,
    );
    checks.push({
      id: "ni-numbers",
      label: "National Insurance Numbers",
      description: "All TRONC-eligible employees have NI numbers recorded",
      status: missingNI.length === 0 ? "pass" : "warning",
      detail:
        missingNI.length === 0
          ? "All eligible employees have NI numbers"
          : `${missingNI.length} employee(s) missing NI numbers`,
      count: missingNI.length,
    });

    // 2. No undistributed TRONC older than 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const stalePending = pending.filter(
      (r) => new Date(r.periodStart) < thirtyDaysAgo,
    );
    checks.push({
      id: "stale-pending",
      label: "Timely Distribution",
      description: "All TRONC is distributed within 30 days",
      status: stalePending.length === 0 ? "pass" : "warning",
      detail:
        stalePending.length === 0
          ? "No stale undistributed records"
          : `${stalePending.length} record(s) older than 30 days awaiting distribution`,
      count: stalePending.length,
    });

    // 3. Eligibility coverage
    const eligibilityRate =
      allEmployees.length > 0
        ? (eligibleEmployees.length /
            allEmployees.filter((e) => e.isActive).length) *
          100
        : 0;
    checks.push({
      id: "eligibility",
      label: "Eligibility Coverage",
      description: "Percentage of active employees enrolled in TRONC scheme",
      status:
        eligibilityRate >= 80
          ? "pass"
          : eligibilityRate >= 50
            ? "info"
            : "warning",
      detail: `${eligibilityRate.toFixed(0)}% of active employees are TRONC-eligible`,
      count: null,
    });

    // 4. Distribution records exist this tax year
    checks.push({
      id: "tax-year",
      label: `Tax Year ${taxYear.label} Records`,
      description: "Distribution records exist for the current tax year",
      status: taxYearDistributed.length > 0 ? "pass" : "info",
      detail:
        taxYearDistributed.length > 0
          ? `${taxYearDistributed.length} distributions totalling £${taxYearTotal.toFixed(2)}`
          : "No distributions yet this tax year",
      count: null,
    });

    return checks;
  }, [
    eligibleEmployees,
    pending,
    allEmployees,
    taxYearDistributed,
    taxYearTotal,
    taxYear,
  ]);

  const passCount = complianceChecks.filter((c) => c.status === "pass").length;

  const handleExportCompliance = () => {
    const { from, to } = taxYear;
    const headers = [
      "Report Type",
      "Tenant",
      "Tax Year",
      "Generated",
      "Total Distributed (£)",
      "Total Pending (£)",
      "Eligible Employees",
      "Total Active Employees",
    ];
    const row = [
      "HMRC TRONC Compliance Summary",
      selectedTenant?.name || "—",
      taxYear.label,
      new Date().toLocaleString("en-GB"),
      totalDistributed.toFixed(2),
      totalPending.toFixed(2),
      eligibleEmployees.length,
      allEmployees.filter((e) => e.isActive).length,
    ];

    const empHeaders = [
      "Employee",
      "Payroll No",
      "NI Number",
      "Job Title",
      "Eligible",
      "Active",
    ];
    const empRows = allEmployees
      .filter((e) => e.isActive)
      .map((e) => [
        `${e.firstName} ${e.lastName}`,
        e.payrollNumber || "—",
        e.nationalInsuranceNumber || "MISSING",
        e.jobTitle || "—",
        e.isEligibleForTronc ? "Yes" : "No",
        e.isActive ? "Yes" : "No",
      ]);

    const csv = [
      headers,
      row,
      [],
      ["EMPLOYEE ELIGIBILITY REGISTER"],
      empHeaders,
      ...empRows,
    ]
      .map((r) => r.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hmrc-tronc-compliance-${taxYear.label.replace("/", "-")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pass":
        return { icon: "CheckCircle2", color: "text-success" };
      case "warning":
        return { icon: "AlertTriangle", color: "text-warning" };
      case "info":
        return { icon: "Info", color: "text-primary" };
      default:
        return { icon: "Circle", color: "text-muted-foreground" };
    }
  };

  const sections = [
    { id: "overview", label: "Compliance Overview", icon: "Shield" },
    { id: "eligibility", label: "Employee Eligibility", icon: "Users" },
    { id: "audit", label: "Distribution Audit", icon: "FileSearch" },
  ];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-base ${section === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
            >
              <Icon name={s.icon} size={14} />
              {s.label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Download"
          iconPosition="left"
          onClick={handleExportCompliance}
        >
          Export Compliance Report
        </Button>
      </div>

      {/* Tax year banner */}
      <div className="card p-4 flex items-start gap-3 border-l-4 border-warning">
        <Icon
          name="Calendar"
          size={18}
          className="text-warning shrink-0 mt-0.5"
        />
        <div>
          <p className="text-sm font-semibold text-foreground">
            UK Tax Year {taxYear.label}
          </p>
          <p className="text-xs text-muted-foreground">
            6 April {taxYear.from.getFullYear()} – 5 April{" "}
            {taxYear.to.getFullYear()}. HMRC requires TRONC scheme operators to
            maintain records of all distributions.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading compliance data...</p>
        </div>
      ) : (
        <>
          {/* OVERVIEW */}
          {section === "overview" && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Compliance Score
                  </p>
                  <p
                    className={`text-2xl font-bold ${passCount === complianceChecks.length ? "text-success" : "text-warning"}`}
                  >
                    {passCount}/{complianceChecks.length}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Tax Year Total
                  </p>
                  <p className="text-xl font-bold text-foreground">
                    £{taxYearTotal.toFixed(2)}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Eligible Staff
                  </p>
                  <p className="text-xl font-bold text-foreground">
                    {eligibleEmployees.length}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Pending TRONC
                  </p>
                  <p
                    className={`text-xl font-bold ${totalPending > 0 ? "text-warning" : "text-foreground"}`}
                  >
                    £{totalPending.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Checks */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <h4 className="text-sm font-semibold">Compliance Checks</h4>
                </div>
                <div className="divide-y divide-border">
                  {complianceChecks.map((check) => {
                    const st = getStatusIcon(check.status);
                    return (
                      <div
                        key={check.id}
                        className="px-5 py-4 flex items-start gap-4"
                      >
                        <div className={`mt-0.5 ${st.color}`}>
                          <Icon name={st.icon} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">
                            {check.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {check.description}
                          </p>
                          <p
                            className={`text-xs mt-1 ${check.status === "pass" ? "text-success" : check.status === "warning" ? "text-warning" : "text-primary"}`}
                          >
                            {check.detail}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ELIGIBILITY */}
          {section === "eligibility" && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="card p-4 text-center border-l-4 border-success">
                  <p className="text-xs text-muted-foreground mb-1">Eligible</p>
                  <p className="text-xl font-bold text-success">
                    {eligibleEmployees.length}
                  </p>
                </div>
                <div className="card p-4 text-center border-l-4 border-warning">
                  <p className="text-xs text-muted-foreground mb-1">
                    Not Eligible
                  </p>
                  <p className="text-xl font-bold text-warning">
                    {ineligibleEmployees.length}
                  </p>
                </div>
                <div className="card p-4 text-center border-l-4 border-muted-foreground">
                  <p className="text-xs text-muted-foreground mb-1">Inactive</p>
                  <p className="text-xl font-bold text-muted-foreground">
                    {inactiveEmployees.length}
                  </p>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <h4 className="text-sm font-semibold">
                    Employee Eligibility Register
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground">
                          Employee
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden sm:table-cell">
                          Payroll No
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden md:table-cell">
                          NI Number
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden lg:table-cell">
                          Job Title
                        </th>
                        <th className="text-center px-5 py-3 text-xs font-semibold text-foreground">
                          Eligible
                        </th>
                        <th className="text-center px-5 py-3 text-xs font-semibold text-foreground">
                          Active
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {allEmployees.map((emp) => (
                        <tr
                          key={emp.id}
                          className="hover:bg-muted/30 transition-base"
                        >
                          <td className="px-5 py-3 text-sm font-semibold text-foreground">
                            {emp.firstName} {emp.lastName}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground font-mono hidden sm:table-cell">
                            {emp.payrollNumber || "—"}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground font-mono hidden md:table-cell">
                            {emp.nationalInsuranceNumber ? (
                              <span className="text-success">Recorded</span>
                            ) : (
                              <span className="text-warning">Missing</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm text-muted-foreground hidden lg:table-cell">
                            {emp.jobTitle || "—"}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {emp.isEligibleForTronc ? (
                              <Icon
                                name="CheckCircle2"
                                size={16}
                                className="text-success inline"
                              />
                            ) : (
                              <Icon
                                name="XCircle"
                                size={16}
                                className="text-warning inline"
                              />
                            )}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {emp.isActive ? (
                              <Icon
                                name="CheckCircle2"
                                size={16}
                                className="text-success inline"
                              />
                            ) : (
                              <Icon
                                name="XCircle"
                                size={16}
                                className="text-muted-foreground inline"
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {allEmployees.length === 0 && (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    No employees found.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AUDIT */}
          {section === "audit" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Distributed
                  </p>
                  <p className="text-xl font-bold text-success">
                    {distributed.length}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Pending</p>
                  <p
                    className={`text-xl font-bold ${pending.length > 0 ? "text-warning" : "text-foreground"}`}
                  >
                    {pending.length}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Total Distributed (£)
                  </p>
                  <p className="text-xl font-bold text-foreground">
                    £{totalDistributed.toFixed(2)}
                  </p>
                </div>
                <div className="card p-4 text-center">
                  <p className="text-xs text-muted-foreground mb-1">
                    Total Pending (£)
                  </p>
                  <p className="text-xl font-bold text-foreground">
                    £{totalPending.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                  <h4 className="text-sm font-semibold">
                    Distribution Audit Trail
                  </h4>
                </div>
                {distributed.length === 0 && pending.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    No TRONC records found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-foreground">
                            Date
                          </th>
                          <th className="text-right px-5 py-3 text-xs font-semibold text-foreground">
                            Amount
                          </th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden sm:table-cell">
                            Location
                          </th>
                          <th className="text-center px-5 py-3 text-xs font-semibold text-foreground">
                            Status
                          </th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-foreground hidden md:table-cell">
                            Method
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {[...distributed, ...pending]
                          .sort(
                            (a, b) =>
                              new Date(b.periodStart) - new Date(a.periodStart),
                          )
                          .slice(0, 50)
                          .map((r) => {
                            const isDist =
                              r.isDistributed ||
                              r.status === "Distributed" ||
                              r.status === 3;
                            return (
                              <tr
                                key={r.id}
                                className="hover:bg-muted/30 transition-base"
                              >
                                <td className="px-5 py-3 text-sm text-foreground">
                                  {new Date(r.periodStart).toLocaleDateString(
                                    "en-GB",
                                    {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    },
                                  )}
                                </td>
                                <td className="px-5 py-3 text-sm font-semibold text-foreground text-right">
                                  £{(r.amount || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                                  {r.locationName || "—"}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isDist ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}
                                  >
                                    <Icon
                                      name={isDist ? "CheckCircle2" : "Clock"}
                                      size={12}
                                    />
                                    {isDist ? "Distributed" : "Pending"}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-sm text-muted-foreground hidden md:table-cell">
                                  {r.distributionRuleName ||
                                    r.distributionMethod ||
                                    "—"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HmrcComplianceTab;
